import { useEffect, useRef } from 'react';

/**
 * The phone's «Назад» closes an open sheet instead of leaving the page.
 *
 * Igor, 10.10 (Redmi, Android Chrome): on /plans the system Back button closed
 * the payment sheet AND took the buyer off /plans — the sheet is not a page,
 * so Back went to whatever was before it. Opening a sheet now pushes ONE
 * history entry for the same URL; Back pops that entry and closes the sheet,
 * and the buyer is still on /plans.
 *
 * ## The rules, and why each exists
 *
 *  - **One entry for all open sheets, not one per sheet.** /plans swaps sheets
 *    in a single render (the one-time sheet hands over to the card sheet, the
 *    gateway radio swaps Payme ⇄ Octobank). Per-sheet entries would `back()`
 *    for the outgoing one while pushing for the incoming one, and the async
 *    `back()` would then pop the NEW sheet's entry and close it. So the entry
 *    is module-level: pushed when the first sheet opens, popped only when the
 *    last one has closed — checked a tick later, after the swap has settled.
 *  - **Closing any other way pops the entry again** (✕, overlay, swipe, a
 *    finished payment) — but only while it is still the CURRENT history entry.
 *    A sheet that closes because the app navigated (a route change, the hosted
 *    pay redirect) must never undo that navigation with a `back()`.
 *  - **The entry keeps the router's state** (`{ ...history.state }`), so
 *    react-router's own popstate handling sees the same location and key and
 *    simply stays on the page.
 */

const MARK = '__uztSheet';

const open = new Map<number, () => void>();
let entryPushed = false;
let nextId = 1;

function entryIsCurrent(): boolean {
  const s = window.history.state as Record<string, unknown> | null;
  return Boolean(s && s[MARK]);
}

function onPopState(): void {
  if (!entryPushed || entryIsCurrent()) return;
  // Back left our entry: every open sheet closes, and the entry is gone.
  entryPushed = false;
  window.removeEventListener('popstate', onPopState);
  for (const close of [...open.values()]) close();
}

function register(id: number, onBack: () => void): void {
  open.set(id, onBack);
  if (entryPushed) return;
  const base = (window.history.state ?? {}) as Record<string, unknown>;
  window.history.pushState({ ...base, [MARK]: true }, '');
  entryPushed = true;
  window.addEventListener('popstate', onPopState);
}

function unregister(id: number): void {
  if (!open.delete(id)) return;
  // A tick later: a sheet that replaces this one in the same render has
  // registered by then, and the entry is still in use.
  window.setTimeout(() => {
    if (open.size > 0 || !entryPushed) return;
    entryPushed = false;
    window.removeEventListener('popstate', onPopState);
    if (entryIsCurrent()) window.history.back();
  }, 0);
}

export function useBackClosesSheet(isOpen: boolean, onBack: () => void): void {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') return;
    const id = nextId++;
    register(id, () => onBackRef.current());
    return () => unregister(id);
  }, [isOpen]);
}

/** Test hook: forget every registration (jsdom keeps module state between tests). */
export function resetBackClosesSheetForTests(): void {
  open.clear();
  entryPushed = false;
  window.removeEventListener('popstate', onPopState);
}
